import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import KbAssistantMarkdown from '../../../components/KbAssistantMarkdown';
import { changesApi } from '../../../services/changesApi';
import type { ChangeAssistantHistoryMessage } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';

type ChatTurn = ChangeAssistantHistoryMessage & { id: string };

type Props = {
  changeId: number;
  active: boolean;
};

const ChangeAssistantTab: React.FC<Props> = ({ changeId, active }) => {
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryAt, setSummaryAt] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [question, setQuestion] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingAsk, setLoadingAsk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    setSummary(null);
    setSummaryAt(null);
    setTurns([]);
    setQuestion('');
    setError(null);
    setUnavailable(false);
  }, [changeId]);

  const handleApiError = useCallback((err: unknown, fallback: string) => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 503) {
      setUnavailable(true);
      setError(getApiErrorMessage(err, fallback));
      return;
    }
    setError(getApiErrorMessage(err, fallback));
  }, []);

  const runSummary = async () => {
    if (!active) return;
    setLoadingSummary(true);
    setError(null);
    setUnavailable(false);
    try {
      const data = await changesApi.postChangeAssistantSummary(changeId);
      setSummary(data.answer);
      setSummaryAt(data.generated_at);
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível gerar o resumo.');
    } finally {
      setLoadingSummary(false);
    }
  };

  const submitQuestion = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const q = question.trim();
    if (!q || loadingAsk || !active) return;
    setLoadingAsk(true);
    setError(null);
    setUnavailable(false);
    const userTurn: ChatTurn = { id: `u-${Date.now()}`, role: 'user', content: q };
    setTurns((prev) => [...prev, userTurn]);
    setQuestion('');
    try {
      const history: ChangeAssistantHistoryMessage[] = [...turns, userTurn].map(({ role, content }) => ({
        role,
        content,
      }));
      const data = await changesApi.postChangeAssistantAsk(changeId, {
        question: q,
        history: history.slice(0, -1),
      });
      setTurns((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: 'assistant', content: data.answer },
      ]);
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível obter resposta.');
    } finally {
      setLoadingAsk(false);
    }
  };

  const onQuestionKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submitQuestion();
    }
  };

  if (!active) {
    return null;
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <p className="text-muted small mb-3">
          Respostas com base nos dados desta mudança (ficha, subtarefas, aprovações, reuniões, pauta e
          textos da apresentação CAB). Não substituem o registro oficial. Imagens do deck não são
          interpretadas visualmente nesta versão.
        </p>

        {error ? (
          <Alert variant={unavailable ? 'warning' : 'danger'} dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            variant="primary"
            disabled={loadingSummary || unavailable}
            onClick={() => void runSummary()}
          >
            {loadingSummary ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Gerando resumo…
              </>
            ) : (
              'Gerar resumo'
            )}
          </Button>
        </div>

        {summary ? (
          <div className="mb-4 p-3 bg-light rounded border">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small">Resumo executivo</strong>
              {summaryAt ? (
                <span className="small text-muted">
                  {new Date(summaryAt).toLocaleString('pt-BR')}
                </span>
              ) : null}
            </div>
            <KbAssistantMarkdown text={summary} />
          </div>
        ) : null}

        <h3 className="h6">Perguntas sobre esta mudança</h3>
        {turns.length > 0 ? (
          <div className="mb-3 d-flex flex-column gap-2">
            {turns.map((turn) => (
              <div
                key={turn.id}
                className={`p-2 rounded border ${turn.role === 'user' ? 'bg-white' : 'bg-light'}`}
              >
                <div className="small fw-semibold mb-1">
                  {turn.role === 'user' ? 'Você' : 'Assistente'}
                </div>
                {turn.role === 'assistant' ? (
                  <KbAssistantMarkdown text={turn.content} />
                ) : (
                  <p className="mb-0 small">{turn.content}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="small text-muted">
            Ex.: &quot;Quais aprovações ainda estão pendentes?&quot; ou &quot;Em qual reunião esta
            mudança entrou?&quot;
          </p>
        )}

        <Form onSubmit={(e) => void submitQuestion(e)}>
          <Form.Group className="mb-2">
            <Form.Control
              as="textarea"
              rows={3}
              value={question}
              placeholder="Pergunte só sobre esta mudança…"
              disabled={loadingAsk || unavailable}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onQuestionKeyDown}
            />
            <Form.Text>
              <kbd>Enter</kbd> envia · <kbd>Shift</kbd> + <kbd>Enter</kbd> quebra linha
            </Form.Text>
          </Form.Group>
          <Button type="submit" variant="outline-primary" disabled={loadingAsk || unavailable || !question.trim()}>
            {loadingAsk ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Enviando…
              </>
            ) : (
              'Enviar pergunta'
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ChangeAssistantTab;
